// Customer detail page — M2 Phase 5.2. Route: /customers/:id
//
// Layout:
//   - CustomerBanner at top
//   - Tabs: Overview · Quotes · Orders · Activity · Notes
//     (Overview + Quotes + Orders + Activity wired; Notes points back at Overview)
//   - Overview tab:
//     * Two-column grid — "Contact info" + "Health"
//     * Below: chronological notes log + add-note input (writes via updateCustomer)
//   - Quotes/Orders tabs: lazy-fetched on first open (per-tab cache in state so
//     toggling between tabs doesn't re-fetch). NewQuoteModal seeded with this
//     customer fires from the Quotes empty-state's "+ New quote" button.
//   - Activity tab: chronological merge of customer + per-quote + per-order
//     audit trails. Reuses the Quotes/Orders caches when available; eagerly
//     fetches both if the user opens Activity first.
//
// Edit-customer button is stubbed (alert) — full edit modal is a future pass.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCustomer,
  listOrders,
  listQuotesByCustomer,
  updateCustomer,
} from '@/shared/crm';
import { currentFirebaseUser } from '@/shared/firebase';
import type {
  Address,
  AuditEntry,
  Contact,
  Customer,
  CustomerGrade,
  CustomerNoteEntry,
  Order,
  OrderStatus,
  Quote,
} from '@/shared/crmTypes';
import { CustomerBanner } from '@/components/CustomerBanner';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';
import { NewQuoteModal } from '@/components/NewQuoteModal';

type Tab = 'overview' | 'quotes' | 'orders' | 'activity' | 'notes';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'quotes', label: 'Quotes' },
  { key: 'orders', label: 'Orders' },
  { key: 'activity', label: 'Activity' },
  { key: 'notes', label: 'Notes' },
];

const TIER_DESCRIPTIONS: Record<Customer['tier'], string> = {
  vip: 'Top-tier customer — highest service level, priority handling.',
  good: 'Reliable customer — standard pricing, warm follow-up tone.',
  slow_pay: 'Pays but late — carrying cost factored into pricing.',
  bad: 'Quote requires Justin approval before sending; AR no credit.',
  new: 'New customer — no history yet, awaiting first job.',
};

/**
 * Per-tab cached fetch state. `data === null` + `loading === false` means
 * "never fetched yet" — distinct from "fetched and got an empty list" which
 * is `data === []`. The cache is keyed on customer.id via the outer effect
 * (cleared when the route param changes).
 */
interface TabCache<T> {
  data: T[] | null;
  loading: boolean;
  error: string | null;
}

function emptyCache<T>(): TabCache<T> {
  return { data: null, loading: false, error: null };
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');

  // Per-tab fetched data — cached in component state so toggling between
  // tabs doesn't trigger a re-fetch. Cleared whenever the customer id changes.
  const [quoteCache, setQuoteCache] = useState<TabCache<Quote>>(emptyCache<Quote>());
  const [orderCache, setOrderCache] = useState<TabCache<Order>>(emptyCache<Order>());

  // "Have we ever kicked off a fetch for this customer.id?" — refs, not state,
  // so flipping them doesn't trigger a re-render or get tangled in the lazy
  // effect's dep array. Reset alongside the caches when the route param flips.
  const quoteFetched = useRef(false);
  const orderFetched = useRef(false);

  // Epoch counters — incremented each time a fetch starts. Promises capture
  // their epoch and ignore their own result if a newer fetch has started
  // (customer navigation, Retry click, etc.). Refs because we never want
  // these to trigger renders.
  const quoteEpoch = useRef(0);
  const orderEpoch = useRef(0);

  // Pre-seeded "+ New quote" modal — fired from the Quotes empty state and
  // the empty-list "+ New quote" button. Routes the user to /quotes/:newId
  // on success so the actual building can happen there.
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const c = await getCustomer(id);
      setCustomer(c);
    } catch (e) {
      setError(`Failed to load customer: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Drop the per-tab caches + "have we fetched" sentinels + bump the
    // epochs so any still-in-flight fetch from the previous customer
    // gets ignored when its promise eventually resolves.
    setQuoteCache(emptyCache<Quote>());
    setOrderCache(emptyCache<Order>());
    quoteFetched.current = false;
    orderFetched.current = false;
    quoteEpoch.current += 1;
    orderEpoch.current += 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ───── Lazy fetch: quotes ─────
  // Fires the first time the Quotes or Activity tab opens (Activity needs
  // quotes for the merged audit feed). The `quoteFetched` ref gates this
  // to a single fetch per customer.id — the Retry handler resets both the
  // ref and the cache state so the next render kicks off a new fetch.
  //
  // Implementation notes:
  //   - Deps are intentionally [tab, customer.id] — NOT on cache state. If
  //     we included cache state, the in-effect setQuoteCache({loading:true})
  //     would re-trigger the effect mid-flight.
  //   - We do NOT cancel the promise on cleanup. Cleanup fires every time
  //     deps change (e.g. tab switches away then back, or React strict-mode
  //     dev remount). Cancelling on cleanup would silently drop legitimate
  //     results. Instead we stamp the customer.id we fetched FOR and ignore
  //     responses if the customer has changed since.
  useEffect(() => {
    if (!customer) return;
    if (tab !== 'quotes' && tab !== 'activity') return;
    if (quoteFetched.current) return;
    quoteFetched.current = true;
    quoteEpoch.current += 1;
    const myEpoch = quoteEpoch.current;
    setQuoteCache({ data: null, loading: true, error: null });
    listQuotesByCustomer(customer.id)
      .then((list) => {
        if (myEpoch !== quoteEpoch.current) return; // a newer fetch superseded us
        setQuoteCache({ data: list, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (myEpoch !== quoteEpoch.current) return;
        // Reset the sentinel on failure so Retry (which clears both cache
        // and ref) fires a fresh fetch.
        quoteFetched.current = false;
        setQuoteCache({
          data: null,
          loading: false,
          error: `Failed to load quotes: ${(e as Error).message}`,
        });
      });
  }, [tab, customer]);

  // ───── Lazy fetch: orders ─────
  // Same shape as the quotes effect — see comment above for the dep-array
  // and `*Fetched` / `*Epoch` ref discipline.
  useEffect(() => {
    if (!customer) return;
    if (tab !== 'orders' && tab !== 'activity') return;
    if (orderFetched.current) return;
    orderFetched.current = true;
    orderEpoch.current += 1;
    const myEpoch = orderEpoch.current;
    setOrderCache({ data: null, loading: true, error: null });
    listOrders({ customerId: customer.id })
      .then((list) => {
        if (myEpoch !== orderEpoch.current) return;
        setOrderCache({ data: list, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (myEpoch !== orderEpoch.current) return;
        orderFetched.current = false;
        setOrderCache({
          data: null,
          loading: false,
          error: `Failed to load orders: ${(e as Error).message}`,
        });
      });
  }, [tab, customer]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
          Loading customer…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={load}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/customers')}
          className="text-sm text-orange hover:underline"
        >
          ← Back to customers
        </button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">🔍</div>
          <div className="font-semibold text-navy mb-1">Customer not found</div>
          <p className="text-sm text-text-mute mb-4">
            ID <span className="font-mono">{id}</span> doesn't exist or was soft-deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            ← Back to customers
          </button>
        </div>
      </div>
    );
  }

  function handleEdit() {
    // Full edit modal is a future pass — stub for now per spec.
    alert('TODO: full customer edit modal (M2 Phase 5.2.x)');
  }

  return (
    <div className="max-w-6xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/customers')}
        className="text-xs text-text-mute hover:text-orange mb-2"
      >
        ← All customers
      </button>

      <CustomerBanner customer={customer} onEdit={handleEdit} />

      {/* Tabs */}
      <div className="border-b border-line mb-4 flex flex-wrap">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? 'border-orange text-orange'
                  : 'border-transparent text-text-mute hover:text-navy'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && <OverviewTab customer={customer} onChanged={load} />}
      {tab === 'quotes' && (
        <QuotesTab
          customer={customer}
          cache={quoteCache}
          onRetry={() => {
            quoteFetched.current = false;
            quoteEpoch.current += 1;
            setQuoteCache(emptyCache<Quote>());
          }}
          onNewQuote={() => setNewQuoteOpen(true)}
          onOpenQuote={(q) => navigate(`/quotes/${q.id}`)}
        />
      )}
      {tab === 'orders' && (
        <OrdersTab
          cache={orderCache}
          onRetry={() => {
            orderFetched.current = false;
            orderEpoch.current += 1;
            setOrderCache(emptyCache<Order>());
          }}
          onOpenOrder={(o) => navigate(`/orders/${o.id}`)}
        />
      )}
      {tab === 'activity' && (
        <ActivityTab
          customer={customer}
          quoteCache={quoteCache}
          orderCache={orderCache}
          onRetry={() => {
            quoteFetched.current = false;
            orderFetched.current = false;
            quoteEpoch.current += 1;
            orderEpoch.current += 1;
            setQuoteCache(emptyCache<Quote>());
            setOrderCache(emptyCache<Order>());
          }}
        />
      )}
      {tab === 'notes' && <NotesTabRedirect onGoToOverview={() => setTab('overview')} />}

      <NewQuoteModal
        open={newQuoteOpen}
        onClose={() => setNewQuoteOpen(false)}
        onCreated={(id) => navigate(`/quotes/${id}`)}
        initialCustomerId={customer.id}
      />
    </div>
  );
}

/** Notes tab shim — Overview already owns the notes card per the M2 Phase 5.2
 *  layout. Keep a clear pointer rather than duplicating the editor here. */
function NotesTabRedirect({ onGoToOverview }: { onGoToOverview: () => void }) {
  return (
    <div className="bg-paper border border-line rounded p-8 text-center text-text-mute">
      <div className="text-4xl mb-2">📝</div>
      <div className="font-semibold text-navy mb-1">Notes live on Overview</div>
      <p className="text-sm mb-4">
        The notes log + add-note input is in the Overview tab for now.
      </p>
      <button
        type="button"
        onClick={onGoToOverview}
        className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
      >
        Take me to Overview
      </button>
    </div>
  );
}

interface OverviewTabProps {
  customer: Customer;
  onChanged: () => void | Promise<void>;
}

function OverviewTab({ customer, onChanged }: OverviewTabProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ContactInfoCard customer={customer} />
        <HealthCard customer={customer} />
      </div>
      <NotesCard customer={customer} onChanged={onChanged} />
    </div>
  );
}

// =====================================================
// Quotes / Orders / Activity tabs — small, shared layout
// =====================================================

/** Generic tab states reused across Quotes / Orders / Activity. */
function TabLoading({ label }: { label: string }) {
  return (
    <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
      Loading {label}…
    </div>
  );
}

function TabError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm flex items-center justify-between gap-3">
      <span>⚠ {message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="text-xs text-orange font-semibold hover:underline shrink-0"
      >
        Retry
      </button>
    </div>
  );
}

/**
 * "in 3 days" / "today" / "5 days overdue" / "—". Negative diff = overdue.
 * Kept here (rather than promoted to a shared util) until a second consumer
 * shows up — easier to inline-edit the copy while the UI is settling.
 */
function relativeDays(iso: string | undefined): { text: string; tone: 'mute' | 'amber' | 'orange' } {
  if (!iso) return { text: '—', tone: 'mute' };
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return { text: '—', tone: 'mute' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return { text: 'today', tone: 'amber' };
  if (diffDays > 0) {
    const tone = diffDays <= 3 ? 'amber' : 'mute';
    return { text: `in ${diffDays} day${diffDays === 1 ? '' : 's'}`, tone };
  }
  const overdue = Math.abs(diffDays);
  return { text: `${overdue} day${overdue === 1 ? '' : 's'} overdue`, tone: 'orange' };
}

function toneClass(tone: 'mute' | 'amber' | 'orange'): string {
  if (tone === 'orange') return 'text-orange font-semibold';
  if (tone === 'amber') return 'text-amber font-semibold';
  return 'text-text-mute';
}

/** Best-effort quote total — prefers importedTotal for bootstrap rows. */
function quoteTotal(q: Quote): number | undefined {
  if (q.imported && q.importedTotal !== undefined) return q.importedTotal;
  return q.totalsByTier?.[0];
}

// ───── Quotes tab ─────

interface QuotesTabProps {
  customer: Customer;
  cache: TabCache<Quote>;
  onRetry: () => void;
  onNewQuote: () => void;
  onOpenQuote: (q: Quote) => void;
}

function QuotesTab({ customer, cache, onRetry, onNewQuote, onOpenQuote }: QuotesTabProps) {
  if (cache.loading) return <TabLoading label="quotes" />;
  if (cache.error) return <TabError message={cache.error} onRetry={onRetry} />;
  const data = cache.data ?? [];
  if (data.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-8 text-center">
        <div className="text-4xl mb-2">📋</div>
        <div className="font-semibold text-navy mb-1">
          No quotes for {customer.companyName} yet.
        </div>
        <p className="text-sm text-text-mute mb-4">
          Spin one up — it'll be pre-seeded with this customer.
        </p>
        <button
          type="button"
          onClick={onNewQuote}
          className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
        >
          + New quote
        </button>
      </div>
    );
  }

  // Newest first — createdAt is always stamped by crm.ts so this sort is stable.
  const sorted = [...data].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs text-text-mute">
          {sorted.length} quote{sorted.length === 1 ? '' : 's'}
        </div>
        <button
          type="button"
          onClick={onNewQuote}
          className="text-xs text-orange font-semibold hover:underline"
        >
          + New quote
        </button>
      </div>
      {sorted.map((q) => (
        <QuoteRow key={q.id} quote={q} onClick={() => onOpenQuote(q)} />
      ))}
    </div>
  );
}

function QuoteRow({ quote, onClick }: { quote: Quote; onClick: () => void }) {
  const total = quoteTotal(quote);
  const due = relativeDays(quote.dueDate);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-paper border border-line rounded p-3 hover:border-orange hover:shadow-sm transition-all"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <QuoteStatusBadge status={quote.status} size="sm" />
        <span className="font-mono font-semibold text-navy">{quote.number}</span>
        <span className="text-xs text-text-mute">{quote.jobType.replace(/_/g, ' ')}</span>
        <div className="flex-1" />
        <span className={`text-xs ${toneClass(due.tone)}`}>{due.text}</span>
        <span className="font-mono text-sm font-semibold text-text min-w-[80px] text-right">
          {fmtCurrency(total)}
        </span>
      </div>
    </button>
  );
}

// ───── Orders tab ─────

interface OrdersTabProps {
  cache: TabCache<Order>;
  onRetry: () => void;
  onOpenOrder: (o: Order) => void;
}

/** Inline status pill — no OrderStatusBadge component exists yet, mirror
 *  QuoteStatusBadge's color rules for the five OrderStatus values. */
const ORDER_STATUS_PILL: Record<OrderStatus, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-amber/15 text-amber' },
  in_production: { label: 'In production', cls: 'bg-orange/15 text-orange' },
  shipped: { label: 'Shipped', cls: 'bg-blue/15 text-blue' },
  closed: { label: 'Closed', cls: 'bg-green/15 text-green' },
  void: { label: 'Void', cls: 'bg-steel/15 text-steel' },
};

function OrderStatusPill({ status }: { status: OrderStatus }) {
  const meta = ORDER_STATUS_PILL[status];
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap text-[10px] px-1.5 py-0.5 ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}

function OrdersTab({ cache, onRetry, onOpenOrder }: OrdersTabProps) {
  if (cache.loading) return <TabLoading label="orders" />;
  if (cache.error) return <TabError message={cache.error} onRetry={onRetry} />;
  const data = cache.data ?? [];
  if (data.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-8 text-center">
        <div className="text-4xl mb-2">📦</div>
        <div className="font-semibold text-navy mb-1">No orders yet.</div>
        <p className="text-sm text-text-mute">
          Orders show up here once a quote converts.
        </p>
      </div>
    );
  }

  const sorted = [...data].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="space-y-2">
      <div className="text-xs text-text-mute mb-1">
        {sorted.length} order{sorted.length === 1 ? '' : 's'}
      </div>
      {sorted.map((o) => (
        <OrderRow key={o.id} order={o} onClick={() => onOpenOrder(o)} />
      ))}
    </div>
  );
}

function OrderRow({ order, onClick }: { order: Order; onClick: () => void }) {
  const shipDue = relativeDays(order.promisedShipDate);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-paper border border-line rounded p-3 hover:border-orange hover:shadow-sm transition-all"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <OrderStatusPill status={order.status} />
        <span className="font-mono font-semibold text-navy">{order.number}</span>
        {order.poNumber ? (
          <span className="text-xs text-text-mute">
            PO <span className="font-mono">{order.poNumber}</span>
          </span>
        ) : (
          <span className="text-xs text-text-mute italic">no PO</span>
        )}
        <div className="flex-1" />
        <span className={`text-xs ${toneClass(shipDue.tone)}`}>
          {order.actualShipDate ? 'shipped' : `ships ${shipDue.text}`}
        </span>
        <span className="font-mono text-sm font-semibold text-text min-w-[80px] text-right">
          {fmtCurrency(order.totalSnapshot)}
        </span>
      </div>
    </button>
  );
}

// ───── Activity tab ─────

interface ActivityTabProps {
  customer: Customer;
  quoteCache: TabCache<Quote>;
  orderCache: TabCache<Order>;
  onRetry: () => void;
}

/**
 * Flattened audit row with the source label & link target carried alongside
 * the AuditEntry so the merged list can render + sort by timestamp directly.
 */
interface ActivityRow {
  ts: string;
  source: string;
  entry: AuditEntry;
}

function ActivityTab({ customer, quoteCache, orderCache, onRetry }: ActivityTabProps) {
  // Activity needs BOTH quote + order caches. If either is still loading,
  // wait. If either errored, surface a combined retry button so the user
  // gets to a sane state in one click.
  if (quoteCache.loading || orderCache.loading) return <TabLoading label="activity" />;
  if (quoteCache.error || orderCache.error) {
    return (
      <TabError
        message={
          quoteCache.error && orderCache.error
            ? `${quoteCache.error} · ${orderCache.error}`
            : (quoteCache.error ?? orderCache.error ?? 'Failed to load activity.')
        }
        onRetry={onRetry}
      />
    );
  }

  const rows: ActivityRow[] = [];
  for (const e of customer._audit ?? []) {
    rows.push({ ts: e.ts, source: 'Customer', entry: e });
  }
  for (const q of quoteCache.data ?? []) {
    for (const e of q._audit ?? []) {
      rows.push({ ts: e.ts, source: `Quote ${q.number}`, entry: e });
    }
  }
  for (const o of orderCache.data ?? []) {
    for (const e of o._audit ?? []) {
      rows.push({ ts: e.ts, source: `Order ${o.number}`, entry: e });
    }
  }

  if (rows.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-8 text-center">
        <div className="text-4xl mb-2">🕒</div>
        <div className="font-semibold text-navy mb-1">No activity recorded yet.</div>
        <p className="text-sm text-text-mute">
          Audit entries land here as the customer, their quotes, and their orders move through the
          pipeline.
        </p>
      </div>
    );
  }

  // Newest first. Stable on identical timestamps (rare but possible in seeds).
  rows.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  return (
    <div className="space-y-1.5">
      <div className="text-xs text-text-mute mb-1">{rows.length} events</div>
      {rows.map((r, i) => (
        <ActivityEntryRow key={`${r.ts}-${i}`} row={r} />
      ))}
    </div>
  );
}

/** Format one audit entry. Action-driven copy so soft-deletes / status changes
 *  read meaningfully without us having to ship a full action dictionary. */
function ActivityEntryRow({ row }: { row: ActivityRow }) {
  const { entry, source } = row;
  const ts = new Date(entry.ts);
  const changed =
    entry.metadata && typeof entry.metadata === 'object' && entry.metadata !== null
      ? (entry.metadata as { changedFields?: unknown }).changedFields
      : undefined;
  const reason =
    entry.metadata && typeof entry.metadata === 'object' && entry.metadata !== null
      ? (entry.metadata as { reason?: unknown }).reason
      : undefined;
  const statusChange =
    entry.action === 'status_change' && entry.fields ? entry.fields.status : undefined;

  return (
    <div className="bg-paper border border-line rounded px-3 py-2 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide font-semibold text-text-mute">
          {entry.action}
        </span>
        <span className="text-xs font-semibold text-navy">{source}</span>
        <div className="flex-1" />
        <time
          dateTime={entry.ts}
          title={ts.toLocaleString()}
          className="text-[11px] text-text-mute font-mono"
        >
          {ts.toLocaleString()}
        </time>
      </div>
      <div className="flex items-center gap-2 mt-1 text-xs text-text-mute flex-wrap">
        <span>by {entry.user}</span>
        {entry.source && entry.source !== 'ui' && (
          <span className="opacity-60">· via {entry.source}</span>
        )}
        {statusChange && (
          <span>
            · <span className="font-mono">{String(statusChange.from)}</span> →{' '}
            <span className="font-mono">{String(statusChange.to)}</span>
          </span>
        )}
        {Array.isArray(changed) && changed.length > 0 && (
          <span>
            · changed:{' '}
            <span className="font-mono">{(changed as string[]).join(', ')}</span>
          </span>
        )}
        {typeof reason === 'string' && reason && (
          <span className="italic">· {reason}</span>
        )}
      </div>
    </div>
  );
}

function fmtAddress(addr: Address | undefined): string {
  if (!addr) return '';
  const parts: string[] = [];
  if (addr.line1) parts.push(addr.line1);
  if (addr.line2) parts.push(addr.line2);
  const cityLine = [addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
  if (cityLine) parts.push(cityLine);
  if (addr.country && addr.country !== 'US') parts.push(addr.country);
  return parts.join('\n');
}

function ContactInfoCard({ customer }: { customer: Customer }) {
  const billing = fmtAddress(customer.billingAddress);
  const shipping = customer.shippingAddress ? fmtAddress(customer.shippingAddress) : '';

  return (
    <div className="bg-paper border border-line rounded p-4">
      <h3 className="font-semibold text-navy mb-3">Contact info</h3>

      <div className="space-y-3 text-sm">
        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
            Billing address
          </div>
          {billing ? (
            <div className="whitespace-pre-line text-text">{billing}</div>
          ) : (
            <div className="text-text-mute italic">No billing address on file</div>
          )}
        </div>

        {shipping && shipping !== billing && (
          <div>
            <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
              Shipping address
            </div>
            <div className="whitespace-pre-line text-text">{shipping}</div>
          </div>
        )}

        <div>
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
            Contacts
          </div>
          {customer.contacts && customer.contacts.length > 0 ? (
            <ul className="space-y-1.5">
              {customer.contacts.map((c) => (
                <ContactRow key={c.id} contact={c} />
              ))}
            </ul>
          ) : (
            <div className="text-text-mute italic">No contacts on file</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-line">
          <div>
            <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-0.5">
              Terms
            </div>
            <div className="font-semibold">{customer.terms}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-0.5">
              Tax exempt
            </div>
            <div className="font-semibold">
              {customer.taxExempt ? (
                <span className="text-green">✓ Exempt</span>
              ) : (
                <span className="text-text-mute">No</span>
              )}
              {customer.taxExemptCert && (
                <span className="ml-2 text-[11px] font-mono text-text-mute">
                  cert: {customer.taxExemptCert}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ contact }: { contact: Contact }) {
  return (
    <li className="border-l-2 border-line pl-2">
      <div className="flex items-center gap-2">
        <span className="font-medium text-text">{contact.name}</span>
        {contact.primary && (
          <span className="text-[10px] bg-orange/15 text-orange px-1.5 py-0.5 rounded font-semibold uppercase">
            Primary
          </span>
        )}
        {contact.title && (
          <span className="text-[11px] text-text-mute">· {contact.title}</span>
        )}
      </div>
      {contact.email && (
        <div className="text-xs font-mono text-text-mute">{contact.email}</div>
      )}
      {contact.phone && (
        <div className="text-xs font-mono text-text-mute">{contact.phone}</div>
      )}
    </li>
  );
}

function HealthCard({ customer }: { customer: Customer }) {
  const grade: CustomerGrade | undefined = customer.grade;
  return (
    <div className="bg-paper border border-line rounded p-4">
      <h3 className="font-semibold text-navy mb-3">Health</h3>
      <div className="text-sm text-text-mute mb-3 leading-snug">
        {TIER_DESCRIPTIONS[customer.tier]}
      </div>

      {customer.tierManualOverride && (
        <div className="bg-amber/10 border-l-4 border-amber text-text px-3 py-1.5 text-xs mb-3">
          Tier locked by manual override
          {customer.tierLastReviewed && (
            <span className="ml-1 opacity-70">
              · reviewed {new Date(customer.tierLastReviewed).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {!grade && (
        <div className="text-xs text-text-mute italic">
          Auto-grade scores haven't been computed yet (M7 analytics layer).
        </div>
      )}

      {grade && (
        <div className="space-y-2">
          <ScoreBar score={grade.score} />
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs pt-2">
            <Metric label="Lifetime revenue" value={fmtCurrency(grade.lifetimeRevenue)} />
            <Metric label="Lifetime margin" value={fmtCurrency(grade.lifetimeMargin)} />
            <Metric label="Avg job size" value={fmtCurrency(grade.averageJobSize)} />
            <Metric
              label="Repeat business"
              value={grade.repeatBusinessCount?.toString() ?? '—'}
            />
            <Metric
              label="Payment timeliness"
              value={
                grade.paymentTimelinessAvg === undefined
                  ? '—'
                  : `${grade.paymentTimelinessAvg > 0 ? '+' : ''}${grade.paymentTimelinessAvg.toFixed(1)}d`
              }
            />
          </div>

          {grade.riskFlags && grade.riskFlags.length > 0 && (
            <div className="pt-2 border-t border-line">
              <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
                Risk flags
              </div>
              <div className="flex flex-wrap gap-1.5">
                {grade.riskFlags.map((flag) => (
                  <span
                    key={flag}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-orange/15 text-orange font-semibold"
                  >
                    {flag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreBar({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const color =
    pct >= 80 ? 'bg-green' : pct >= 60 ? 'bg-amber' : pct >= 40 ? 'bg-amber' : 'bg-orange';
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-text-mute uppercase tracking-wide">
          Grade score
        </span>
        <span className="font-mono font-bold text-text">{pct}/100</span>
      </div>
      <div className="h-2 bg-bg rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-text-mute">{label}</div>
      <div className="font-semibold font-mono text-text">{value}</div>
    </div>
  );
}

function fmtCurrency(n?: number): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

interface NotesCardProps {
  customer: Customer;
  onChanged: () => void | Promise<void>;
}

function NotesCard({ customer, onChanged }: NotesCardProps) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const notes = [...(customer.notes ?? [])].sort(
    (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime(),
  );

  async function addNote() {
    if (!text.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const newEntry: CustomerNoteEntry = {
        ts: new Date().toISOString(),
        user: currentFirebaseUser()?.uid ?? 'system',
        text: text.trim(),
      };
      const merged = [...(customer.notes ?? []), newEntry];
      await updateCustomer(customer.id, { notes: merged });
      setText('');
      await onChanged();
    } catch (e) {
      setErr(`Failed to save note: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-paper border border-line rounded p-4">
      <h3 className="font-semibold text-navy mb-3">Notes</h3>

      <div className="border border-orange/30 bg-orange/5 rounded p-3 mb-3">
        <div className="text-sm font-semibold mb-2">+ Add note</div>
        <div className="flex gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy && text.trim()) void addNote();
            }}
            placeholder="Quick note — call summary, preference, anything…"
            className="flex-1 px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <button
            type="button"
            onClick={addNote}
            disabled={busy || !text.trim()}
            className="px-3 py-1 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add'}
          </button>
        </div>
        {err && (
          <div className="mt-2 text-xs text-orange">⚠ {err}</div>
        )}
      </div>

      {notes.length === 0 ? (
        <div className="text-sm text-text-mute italic text-center py-4">
          No notes yet. Add the first one above.
        </div>
      ) : (
        <ul className="space-y-2">
          {notes.map((n, i) => (
            <li key={`${n.ts}-${i}`} className="border border-line rounded p-2 text-sm">
              <div className="text-text whitespace-pre-wrap break-words">{n.text}</div>
              <div className="text-[11px] text-text-mute mt-1 flex items-center gap-2">
                <span className="font-mono">{new Date(n.ts).toLocaleString()}</span>
                <span className="opacity-60">·</span>
                <span>{n.user}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
